import React, { ReactNode, createContext, useContext } from 'react';

import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import recaptchaService from '../services/recaptcha/recaptcha.service';

interface RecaptchaContextType {
  executeRecaptcha: (action?: string) => Promise<string>;
  isEnabled: boolean;
}

const RecaptchaContext = createContext<RecaptchaContextType | undefined>(undefined);

interface RecaptchaProviderProps {
  children: ReactNode;
}

/**
 * Inner component to access the hook from react-google-recaptcha-v3
 */
const RecaptchaContextContent: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const isEnabled = recaptchaService.isRecaptchaEnabled();

  const execute = async (action: string = 'submit'): Promise<string> => {
    if (!isEnabled) {
      console.warn('reCAPTCHA is disabled or not configured');
      return '';
    }

    if (!executeRecaptcha) {
      console.warn('reCAPTCHA not ready yet');
      return '';
    }

    try {
      const token = await executeRecaptcha(action);
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      return '';
    }
  };

  return (
    <RecaptchaContext.Provider value={{ executeRecaptcha: execute, isEnabled }}>
      {children}
    </RecaptchaContext.Provider>
  );
};

/**
 * Main Provider Component
 */
export const RecaptchaProvider: React.FC<RecaptchaProviderProps> = ({ children }) => {
  const siteKey = recaptchaService.getSiteKey();

  if (!siteKey) {
    // Fallback provider if no key is configured
    return (
      <RecaptchaContext.Provider value={{ executeRecaptcha: async () => '', isEnabled: false }}>
        {children}
      </RecaptchaContext.Provider>
    );
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={siteKey}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head',
      }}
    >
      <RecaptchaContextContent>{children}</RecaptchaContextContent>
    </GoogleReCaptchaProvider>
  );
};

/**
 * Hook to use reCAPTCHA
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useRecaptchaContext = (): RecaptchaContextType => {
  const context = useContext(RecaptchaContext);
  if (context === undefined) {
    throw new Error('useRecaptchaContext must be used within a RecaptchaProvider');
  }
  return context;
};
